import { Injectable } from '@angular/core';
import { ApiService } from '../provider/api.service';
import { AuthService } from './auth.service';
import { endpoints } from '../constant/endpoints';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { first, map } from 'rxjs/operators';

export interface CustomerPreferences {
  allergyIds: Set<number>;
  dislikeIds: Set<number>;
  loaded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerPreferencesService {
  private preferences$ = new BehaviorSubject<CustomerPreferences>({
    allergyIds: new Set(),
    dislikeIds: new Set(),
    loaded: false
  });

  private loadingPromise: Promise<CustomerPreferences> | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  getPreferences(): CustomerPreferences {
    return this.preferences$.getValue();
  }

  observePreferences(): Observable<CustomerPreferences> {
    return this.preferences$.asObservable();
  }

  async loadPreferences(customerId: number): Promise<CustomerPreferences> {
    if (!customerId) {
      console.warn('[CustomerPreferencesService] No customerId provided');
      return this.getPreferences();
    }

    const current = this.getPreferences();
    if (current.loaded) {
      return current;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.fetchPreferences(customerId);
    const result = await this.loadingPromise;
    this.loadingPromise = null;
    return result;
  }

  /**
   * Get selected allergy/dislike IDs from profile (auth user).
   * Profile is the source of truth for what the user has selected.
   */
  private getSelectedIdsFromProfile(): { allergyIds: number[]; dislikeIds: number[] } {
    const profile_data = this.authService.getAuthUser();
    if (!profile_data) return { allergyIds: [], dislikeIds: [] };
    const to_ids = (raw: any): number[] => {
      if (!Array.isArray(raw)) return [];
      return raw.map((x: any) => (typeof x === 'number' ? x : x?.id)).filter((id: any) => id != null);
    };
    const allergy_ids = [...to_ids(profile_data.allergy_ids), ...to_ids(profile_data.allergies)];
    const dislike_ids = [...to_ids(profile_data.dislike_ids), ...to_ids(profile_data.dislikes)];
    return {
      allergyIds: [...new Set(allergy_ids)],
      dislikeIds: [...new Set(dislike_ids)]
    };
  }

  private async fetchPreferences(customerId: number): Promise<CustomerPreferences> {
    try {
      const allergies_url = `${endpoints.allergies}?customer_id=${customerId}`;
      const dislikes_url = `${endpoints.dislikes}?customer_id=${customerId}`;

      const [allergiesResponse, dislikesResponse] = await Promise.all([
        this.apiService.get(allergies_url).pipe(first()).toPromise(),
        this.apiService.get(dislikes_url).pipe(first()).toPromise()
      ]);

      let allergy_ids = new Set<number>(
        (allergiesResponse as any)?.data?.customer_selected_allergies || []
      );
      let dislike_ids = new Set<number>(
        (dislikesResponse as any)?.data?.customer_selected_dislikes || []
      );

      const from_profile = this.getSelectedIdsFromProfile();
      if (from_profile.allergyIds.length > 0 || from_profile.dislikeIds.length > 0) {
        from_profile.allergyIds.forEach((id) => allergy_ids.add(id));
        from_profile.dislikeIds.forEach((id) => dislike_ids.add(id));
      }

      const preferences: CustomerPreferences = {
        allergyIds: allergy_ids,
        dislikeIds: dislike_ids,
        loaded: true
      };

      this.preferences$.next(preferences);
      console.log('[CustomerPreferencesService] Loaded preferences:', {
        allergyCount: allergy_ids.size,
        dislikeCount: dislike_ids.size
      });

      return preferences;
    } catch (error) {
      console.error('[CustomerPreferencesService] Error loading preferences:', error);
      const from_profile = this.getSelectedIdsFromProfile();
      const allergy_ids = new Set<number>(from_profile.allergyIds);
      const dislike_ids = new Set<number>(from_profile.dislikeIds);
      const preferences: CustomerPreferences = {
        allergyIds: allergy_ids,
        dislikeIds: dislike_ids,
        loaded: true
      };
      this.preferences$.next(preferences);
      return preferences;
    }
  }

  async refreshPreferences(customerId: number): Promise<CustomerPreferences> {
    this.preferences$.next({
      allergyIds: new Set(),
      dislikeIds: new Set(),
      loaded: false
    });
    this.loadingPromise = null;
    return this.loadPreferences(customerId);
  }

  clearPreferences(): void {
    this.preferences$.next({
      allergyIds: new Set(),
      dislikeIds: new Set(),
      loaded: false
    });
    this.loadingPromise = null;
  }

  checkRecipeConflict(recipeId: number, isMandatory: boolean): number {
    const prefs = this.getPreferences();
    
    if (prefs.dislikeIds.has(recipeId)) {
      return isMandatory ? 20 : 2;
    }
    if (prefs.allergyIds.has(recipeId)) {
      return isMandatory ? 10 : 1;
    }
    return 0;
  }

checkMenuItemHealthCode(recipes: any[]): number {
  let maxConflictCode = 0;

  const processItems = (items: any[]) => {
    if (!Array.isArray(items)) return;

    for (const item of items) {
      const isMandatory = String(item.is_mandatory_for_menu_item) === '1';
      const conflictCode = this.checkRecipeConflict(Number(item.id), isMandatory);

      if (conflictCode > maxConflictCode) {
        maxConflictCode = conflictCode;
      }

      // Handle the nested structure: item.recipes might be an object containing an array
      if (item.recipes) {
        const nestedList = Array.isArray(item.recipes) 
          ? item.recipes 
          : (item.recipes.recipes || []); // Handles the "recipes: { recipes: [...] }" pattern
        
        processItems(nestedList);
      }
    }
  };

  processItems(recipes);
  return maxConflictCode;
}
}
